import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, FileText, Shield, Cookie, AlertTriangle, ChevronRight } from "lucide-react";

const LAST_UPDATED = "March 30, 2026";

// Legal content data
const legalContent = {
  privacy: {
    title: "Privacy Policy",
    icon: Shield,
    content: `
## 1. Introduction

WornWell ("we", "us", "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you use our website, mobile application, and related services (the "Services").

We handle personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).

By using our Services, you agree to this Privacy Policy.

## 2. Information We Collect

### Information You Provide

We may collect:
- Name and contact details (e.g. email address)
- Account information
- Profile details
- Wardrobe data (including item details, brands, photos, purchase data)
- Marketplace listing information
- Communications with us

### Automatically Collected Information

We may collect:
- Device and browser information
- IP address
- Usage and interaction data
- Cookies and analytics data

### Third-Party Information

We may receive information from:
- Marketplace integrations
- Authentication providers
- Analytics providers
- Payment providers

## 3. How We Use Your Information

We use your information to:
- Provide and operate the Services
- Create and manage your account
- Generate insights and recommendations
- Enable marketplace integrations
- Improve functionality and user experience
- Communicate with you
- Maintain security and prevent fraud
- Comply with legal obligations

## 4. Sharing Your Information

We may share your information with:

### Service Providers

Trusted third parties including:
- Cloud hosting providers
- Analytics providers
- Payment processors
- Email and notification services

These providers only access data necessary to perform services for us.

### Marketplace Integrations

If you choose to list items through integrations, we may share relevant information to complete those listings.

### Legal Requirements

We may disclose information if required by law or regulatory authorities.

### Business Transfers

Your information may be transferred if we undergo a merger, acquisition, or asset sale.

## 5. Overseas Disclosure

Some service providers may be located outside Australia. We take reasonable steps to ensure overseas recipients handle your information in accordance with Australian privacy laws.

## 6. Cookies and Analytics

We use cookies and similar technologies to:
- Maintain login sessions
- Analyse usage
- Improve performance

You can disable cookies in your browser settings, though some features may not function properly.

## 7. Data Security

We take reasonable steps to protect your information, including:
- Secure hosting infrastructure
- Access controls
- Encryption where appropriate

However, no method of transmission is completely secure.

## 8. Data Retention

We retain personal information only as long as necessary to provide our Services, comply with legal obligations, and resolve disputes.

You may request deletion of your account at any time.

## 9. Your Rights

You may request to:
- Access your personal information
- Correct inaccurate information
- Delete your data
- Withdraw consent (where applicable)

To exercise these rights, contact us below.

## 10. Children's Privacy

Our Services are not intended for individuals under 18. We do not knowingly collect information from children.

## 11. Changes to This Policy

We may update this Privacy Policy from time to time. Updates will be posted on this page.

## 12. Contact

**WornWell**
Email: info@wornwell.io
Website: wornwell.io
Location: Australia
    `
  },
  terms: {
    title: "Terms of Service",
    icon: FileText,
    content: `
## 1. Agreement to Terms

These Terms of Service govern your use of WornWell including our website, mobile application, and related services.

By using our Services, you agree to these Terms.

## 2. Eligibility

You must be at least 18 years old to use WornWell.

## 3. Your Account

You are responsible for:
- Maintaining account security
- Keeping login details confidential
- All activity under your account

## 4. Acceptable Use

You agree not to:
- Use the Services unlawfully
- Upload misleading information
- Interfere with platform functionality
- Attempt unauthorized access

## 5. User Content

You retain ownership of content you upload but grant WornWell a non-exclusive licence to store, process, and display content for platform operation.

## 6. Marketplace Integrations

WornWell may integrate with third-party marketplaces. We are not responsible for third-party services or transactions.

## 7. AI Features

WornWell may provide AI-generated insights for informational purposes only. Accuracy is not guaranteed.

## 8. Fees and Subscriptions

Some features may require payment. Pricing may change with notice.

## 9. Intellectual Property

All platform content is owned by WornWell and protected by applicable laws.

## 10. Limitation of Liability

Services are provided "as is". WornWell is not liable for losses or damages arising from use of the Services.

## 11. Termination

We may suspend or terminate access for violations of these Terms.

## 12. Governing Law

These Terms are governed by Australian law.

## 13. Contact

**WornWell**
Email: info@wornwell.io
Website: wornwell.io
    `
  },
  cookies: {
    title: "Cookie Policy",
    icon: Cookie,
    content: `
## 1. Use of Cookies

We use cookies to:
- Maintain login sessions
- Analyse usage
- Improve performance

## 2. Types of Cookies

### Essential Cookies
Required for basic functionality such as authentication and security.

### Analytics Cookies
Help us understand how visitors interact with our Services.

### Performance Cookies
Used to improve site speed and user experience.

## 3. Managing Cookies

You can disable cookies in your browser settings. Note that disabling certain cookies may affect functionality.

### How to Manage Cookies:
- **Chrome**: Settings → Privacy and Security → Cookies
- **Safari**: Preferences → Privacy → Cookies
- **Firefox**: Options → Privacy & Security → Cookies

## 4. Contact

For questions about our cookie practices:

Email: info@wornwell.io
    `
  },
  disclaimer: {
    title: "Disclaimer",
    icon: AlertTriangle,
    content: `
## 1. General Disclaimer

WornWell provides informational insights only. While we strive for accuracy, we cannot guarantee that all information, valuations, or recommendations are correct, complete, or up-to-date.

## 2. No Financial Advice

WornWell does not provide financial, investment, or professional advice. Any valuations, cost-per-wear calculations, or resale estimates are for informational purposes only and should not be relied upon for financial decisions.

## 3. Third-Party Platforms

WornWell may integrate with or provide links to third-party marketplaces and services. We are not responsible for:
- Third-party services, products, or transactions
- Accuracy of third-party information
- Any losses arising from third-party interactions

## 4. AI-Generated Content

Some features use artificial intelligence to generate insights and recommendations. AI-generated content:
- Is provided for informational purposes only
- May not be accurate or complete
- Should not be solely relied upon for decisions

## 5. Limitation of Liability

Use of the Services is at your own risk. To the maximum extent permitted by law, WornWell shall not be liable for any indirect, incidental, special, consequential, or punitive damages.

## 6. Contact

For questions about this disclaimer:

**WornWell**
Email: info@wornwell.io
Website: wornwell.io
    `
  }
};

// Simple markdown-like parser for headings and lists
function renderContent(content) {
  const lines = content.trim().split('\n');
  const elements = [];
  let currentList = [];
  let listKey = 0;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`} className="list-disc pl-6 space-y-1.5 text-muted-foreground mb-4">
          {currentList.map((item) => (
            <li key={item.substring(0, 30)}>{item}</li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    if (!trimmed) {
      flushList();
      return;
    }

    // H2 heading
    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={index} className="font-bold text-lg text-primary mt-8 mb-3 first:mt-0">
          {trimmed.replace('## ', '')}
        </h2>
      );
      return;
    }

    // H3 heading
    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={index} className="font-semibold text-base text-primary mt-5 mb-2">
          {trimmed.replace('### ', '')}
        </h3>
      );
      return;
    }

    // List item
    if (trimmed.startsWith('- ')) {
      currentList.push(trimmed.replace('- ', ''));
      return;
    }

    // Bold text (standalone line)
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      flushList();
      elements.push(
        <p key={index} className="font-semibold text-primary mb-1">
          {trimmed.replace(/\*\*/g, '')}
        </p>
      );
      return;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <p key={index} className="text-muted-foreground leading-relaxed mb-3">
        {trimmed}
      </p>
    );
  });

  flushList();
  return elements;
}

// Legal index page (list of all legal documents)
function LegalIndex() {
  const navigate = useNavigate();
  
  const legalItems = [
    { key: 'privacy', ...legalContent.privacy },
    { key: 'terms', ...legalContent.terms },
    { key: 'cookies', ...legalContent.cookies },
    { key: 'disclaimer', ...legalContent.disclaimer },
  ];

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="legal-page">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/settings')}
          className="gap-2 -ml-2 text-muted-foreground hover:text-primary"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </Button>

        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
            Legal
          </h1>
          <p className="text-muted-foreground">
            Review our policies and terms
          </p>
        </div>

        {/* Legal document links */}
        <div className="space-y-3">
          {legalItems.map((item) => (
            <Link
              key={item.key}
              to={`/legal/${item.key}`}
              className="block"
              data-testid={`legal-${item.key}-link`}
            >
              <Card className="border border-border hover:border-primary/30 hover:bg-sand-50 transition-colors cursor-pointer">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-sand-100 rounded-lg">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium">{item.title}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Contact info */}
        <Card className="border border-border mt-8">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              For legal inquiries, contact us at{' '}
              <a href="mailto:info@wornwell.io" className="text-primary hover:underline">
                info@wornwell.io
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

// Individual legal document page
function LegalDocument() {
  const { type } = useParams();
  const navigate = useNavigate();
  
  const doc = legalContent[type];
  
  if (!doc) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Document Not Found</h1>
          <Button onClick={() => navigate('/legal')}>Back to Legal</Button>
        </div>
      </Layout>
    );
  }

  const Icon = doc.icon;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid={`legal-${type}-page`}>
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/legal')}
          className="gap-2 -ml-2 text-muted-foreground hover:text-primary mb-6"
          data-testid="back-to-legal"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Legal
        </Button>

        {/* Document header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-sand-100 rounded-lg">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl tracking-tight text-primary">
            {doc.title}
          </h1>
        </div>

        {/* Last updated */}
        <p className="text-sm text-muted-foreground mb-8">
          Last Updated: {LAST_UPDATED}
        </p>

        {/* Document content */}
        <div className="prose prose-sm max-w-none">
          {renderContent(doc.content)}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground text-center">
            Questions? Contact us at{' '}
            <a href="mailto:info@wornwell.io" className="text-primary hover:underline">
              info@wornwell.io
            </a>
          </p>
        </div>
      </div>
    </Layout>
  );
}

// Main Legal component - routes between index and document views
export function Legal() {
  const { type } = useParams();
  
  if (type) {
    return <LegalDocument />;
  }
  
  return <LegalIndex />;
}

export default Legal;
